import { BLOCK_CODE_LINE, type BlockCodeLineData, code, MARK_BOLD } from '@gitbook/doc-core';
import { Block, type Inline, Text } from '@gitbook/slate';
import { List } from '@gitbook/util-immutable';

/**
 * Convert a raw set of text and inlines parsed from the inside of the HTML code
 * into a valid set of line blocks.
 */
export function parseCodeBlockLines(inlines: Array<Text | Inline>): Block[] {
    let result: Block[] = [];

    let currentLineHighlighted = false;
    let currentLine: Array<Text | Inline> = [];

    function pushText(text: string, highlighted: boolean) {
        if (text.length > 0) {
            currentLineHighlighted = currentLineHighlighted || highlighted;
        }

        const last = currentLine[currentLine.length - 1];
        if (Text.isText(last)) {
            currentLine[currentLine.length - 1] = last.appendText(text);
        } else {
            currentLine.push(Text.create(text));
        }
    }

    function splitLine() {
        if (currentLine.length === 0) {
            return;
        }

        const data: BlockCodeLineData = {
            ...(currentLineHighlighted ? { highlighted: true } : {}),
        };

        result.push(Block.create({ type: BLOCK_CODE_LINE, nodes: currentLine, data }));
        currentLine = [];
        currentLineHighlighted = false;
    }

    inlines.forEach((node) => {
        if (node.object === 'text') {
            node.leaves.forEach((leaf) => {
                const parts = code.splitTextByNewLines(leaf.text);
                const highlighted = leaf.marks.some((mark) => mark.type === MARK_BOLD);
                parts.forEach((part, index) => {
                    if (index > 0) {
                        splitLine();
                    }

                    pushText(part, highlighted);
                });
            });
        } else {
            currentLine.push(
                node.merge({
                    nodes: List.create([Text.create(node.text)]),
                })
            );
        }
    });

    splitLine();

    // Remove trailing empty lines as code block created by markdown parsing
    // have an additional empty line at the end
    if (result.length > 0 && result[result.length - 1].text === '') {
        result = result.slice(0, -1);
    }

    return result;
}
